import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api, ApiError, errorText } from '../api';
import type { Dublette, Kunde, Telefon } from '../types';
import { Field, Notice, kundenName } from './ui';

interface Daten {
  anrede: string;
  vorname: string;
  nachname: string;
  firma: string;
  strasse: string;
  plz: string;
  ort: string;
  email: string;
  notiz: string;
  telefone: Telefon[];
}

const LEER: Daten = { anrede: '', vorname: '', nachname: '', firma: '', strasse: '', plz: '', ort: '', email: '', notiz: '', telefone: [{ nummer: '', art: 'telefon' }] };

function ausKunde(k: Kunde): Daten {
  const tel = (k.telefone as Telefon[]).map((t) => ({ id: t.id, nummer: t.nummer, art: t.art ?? 'telefon' }));
  return {
    anrede: k.anrede ?? '',
    vorname: k.vorname ?? '',
    nachname: k.nachname ?? '',
    firma: k.firma ?? '',
    strasse: k.strasse ?? '',
    plz: k.plz ?? '',
    ort: k.ort ?? '',
    email: k.email ?? '',
    notiz: k.notiz ?? '',
    telefone: tel.length ? tel : [{ nummer: '', art: 'telefon' }],
  };
}

/**
 * Kunden erfassen oder bearbeiten.
 *
 * Bei der Neuanlage läuft während des Tippens die Dublettensuche mit: Wer
 * einen Namen, eine Nummer oder eine E-Mail eingibt, sieht sofort, ob es den
 * Kunden schon gibt, und übernimmt ihn mit einem Klick - statt ihn ein zweites
 * Mal anzulegen. Der Server prüft beim Speichern noch einmal.
 */
export function KundeFormular({
  kunde,
  vorbelegung,
  onGespeichert,
  onVorhandenen,
  onAbbrechen,
}: {
  kunde?: Kunde;
  vorbelegung?: Partial<Daten>;
  onGespeichert: (k: Kunde) => void;
  onVorhandenen?: (id: string) => void;
  onAbbrechen: () => void;
}) {
  const [d, setD] = useState<Daten>(() => (kunde ? ausKunde(kunde) : { ...LEER, ...vorbelegung }));
  const [dubletten, setDubletten] = useState<Dublette[]>([]);
  const [blockiert, setBlockiert] = useState(false);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);
  const neu = !kunde;
  const zaehler = useRef(0);

  const setze = (feld: keyof Daten, wert: string) => {
    setD((alt) => ({ ...alt, [feld]: wert }));
    setBlockiert(false);
  };

  const telefone = () => d.telefone.filter((t) => t.nummer.trim());

  useEffect(() => {
    if (!neu) return;
    const nr = ++zaehler.current;
    const zeit = window.setTimeout(() => {
      api<Dublette[]>('/api/kunden/dubletten', {
        method: 'POST',
        body: {
          vorname: d.vorname,
          nachname: d.nachname,
          firma: d.firma,
          strasse: d.strasse,
          plz: d.plz,
          email: d.email.includes('@') && d.email.includes('.') ? d.email : undefined,
          telefone: telefone(),
        },
      })
        .then((r) => nr === zaehler.current && setDubletten(r))
        .catch(() => undefined);
    }, 350);
    return () => window.clearTimeout(zeit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neu, d.vorname, d.nachname, d.firma, d.strasse, d.plz, d.email, JSON.stringify(d.telefone)]);

  async function speichern(event: FormEvent | null, trotzdem = false) {
    event?.preventDefault();
    setBusy(true);
    setFehler('');
    const body = { ...d, telefone: telefone(), ...(neu ? { dubletteBestaetigt: trotzdem } : {}) };
    try {
      const k = neu
        ? await api<Kunde>('/api/kunden', { method: 'POST', body })
        : await api<Kunde>(`/api/kunden/${kunde!.id}`, { method: 'PUT', body });
      onGespeichert(k);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const details = err.details as { dubletten?: Dublette[] } | undefined;
        if (details?.dubletten) setDubletten(details.dubletten);
        setBlockiert(true);
      } else {
        setFehler(errorText(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const sicher = dubletten.some((x) => x.punkte >= 80);

  return (
    <form onSubmit={(e) => void speichern(e)} className="stack" style={{ gap: 12 }}>
      {fehler && <Notice kind="error">{fehler}</Notice>}

      {neu && dubletten.length > 0 && (
        <div className={sicher ? 'dubletten sicher' : 'dubletten'}>
          <strong>{sicher ? 'Dieser Kunde ist vermutlich schon erfasst:' : 'Möglicherweise schon vorhanden:'}</strong>
          {dubletten.map((x) => (
            <div className="dublette" key={x.id}>
              <div className="wer">
                <div>
                  <strong>{kundenName(x)}</strong> <span className="muted mono">{x.kundennummer}</span>
                </div>
                <div className="small muted">
                  {[x.strasse, [x.plz, x.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                  {x.telefone.length > 0 && ` · ${x.telefone.join(', ')}`}
                </div>
                <div className="small">{x.gruende.join(' · ')}</div>
              </div>
              {onVorhandenen && (
                <button type="button" className="primary klein" onClick={() => onVorhandenen(x.id)}>
                  Diesen Kunden verwenden
                </button>
              )}
            </div>
          ))}
          {blockiert && (
            <div className="row">
              <span className="small">Wirklich ein anderer Kunde?</span>
              <button type="button" className="klein" disabled={busy} onClick={() => void speichern(null, true)}>
                Trotzdem neu anlegen
              </button>
            </div>
          )}
        </div>
      )}

      <div className="grid3">
        <Field label="Anrede">
          <select value={d.anrede} onChange={(e) => setze('anrede', e.target.value)}>
            <option value="">—</option>
            <option>Frau</option>
            <option>Herr</option>
            <option>Divers</option>
            <option>Firma</option>
          </select>
        </Field>
        <Field label="Vorname">
          <input value={d.vorname} onChange={(e) => setze('vorname', e.target.value)} autoFocus={neu} />
        </Field>
        <Field label="Nachname">
          <input value={d.nachname} onChange={(e) => setze('nachname', e.target.value)} />
        </Field>
      </div>
      <Field label="Firma" hint="Nachname oder Firma ist Pflicht">
        <input value={d.firma} onChange={(e) => setze('firma', e.target.value)} />
      </Field>
      <div className="grid3">
        <div className="span2">
          <Field label="Straße und Hausnummer">
            <input value={d.strasse} onChange={(e) => setze('strasse', e.target.value)} />
          </Field>
        </div>
        <Field label="PLZ">
          <input value={d.plz} onChange={(e) => setze('plz', e.target.value)} inputMode="numeric" maxLength={10} />
        </Field>
      </div>
      <div className="grid2">
        <Field label="Ort">
          <input value={d.ort} onChange={(e) => setze('ort', e.target.value)} />
        </Field>
        <Field label="E-Mail">
          <input type="email" value={d.email} onChange={(e) => setze('email', e.target.value)} />
        </Field>
      </div>

      <div className="field">
        <span>Telefon</span>
        {d.telefone.map((t, i) => (
          <div className="row" key={t.id ?? `neu-${i}`} style={{ flexWrap: 'nowrap' }}>
            <select
              value={t.art ?? 'telefon'}
              style={{ width: 140 }}
              onChange={(e) =>
                setD((alt) => ({ ...alt, telefone: alt.telefone.map((x, j) => (j === i ? { ...x, art: e.target.value as Telefon['art'] } : x)) }))
              }
            >
              <option value="telefon">Telefon</option>
              <option value="mobil">Mobil</option>
              <option value="geschaeftlich">Geschäftlich</option>
            </select>
            <input
              value={t.nummer}
              inputMode="tel"
              onChange={(e) => {
                setBlockiert(false);
                setD((alt) => ({ ...alt, telefone: alt.telefone.map((x, j) => (j === i ? { ...x, nummer: e.target.value } : x)) }));
              }}
            />
            {d.telefone.length > 1 && (
              <button type="button" className="klein" onClick={() => setD((alt) => ({ ...alt, telefone: alt.telefone.filter((_, j) => j !== i) }))}>
                Entfernen
              </button>
            )}
          </div>
        ))}
        <div>
          <button type="button" className="link small" onClick={() => setD((alt) => ({ ...alt, telefone: [...alt.telefone, { nummer: '', art: 'mobil' }] }))}>
            + weitere Nummer
          </button>
        </div>
      </div>

      <Field label="Notiz">
        <textarea value={d.notiz} onChange={(e) => setze('notiz', e.target.value)} style={{ minHeight: 60 }} />
      </Field>

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" onClick={onAbbrechen}>
          Abbrechen
        </button>
        <button type="submit" className="primary" disabled={busy || (neu && blockiert)}>
          {neu ? 'Kunde anlegen' : 'Speichern'}
        </button>
      </div>
    </form>
  );
}
